import React, { useEffect, useState } from "react";
import {
    Button,
    ButtonGroup,
    Col,
    Container,
    Form,
    Modal,
    Row,
    Table,
} from "react-bootstrap";
import {
    Controller,
    SubmitErrorHandler,
    SubmitHandler,
    useForm,
    useWatch,
} from "react-hook-form";
import {
    Link,
    useLoaderData,
    useLocation,
    useNavigate,
} from "react-router-dom";
import Select from "react-select";

import { yupResolver } from "@hookform/resolvers/yup";
import { Elements } from "@stripe/react-stripe-js";
import { StripeElementsOptions, loadStripe } from "@stripe/stripe-js";
import DOMPurify from "dompurify";
import * as yup from "yup";

import CheckoutForm from "./CheckoutForm";

import TournamentOfferings from "../Tournaments/TournamentOfferings";

import { store } from "../..";
import "../../assets/css/wibb/pages/register.scss";
import { alertActions } from "../../features/alert/alertSlice";
import {
    clubhouseActions,
    selectClubhouseTeams,
} from "../../features/clubhouse/clubhouseSlice";
import {
    globalActions,
    selectSetting,
} from "../../features/global/globalSlice";
import {
    selectCart,
    selectProducts,
    shopActions,
} from "../../features/shop/shopSlice";
import { tournamentManagementActions } from "../../features/tourneymgmt/tournamentManagementSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
    createOrder,
    getClubhouseTeams,
    getCoupon,
    getPaymentIntent,
} from "../../lib/api";
import { PHONE_REGEXP, TOURNAMENT_LOGO_URL } from "../../lib/globals";
import { CloseIcon } from "../../lib/icons";
import { defaultDateFormat, displayFormAlert } from "../../lib/utils";
import { IAPIResponse, IPromoCode, ITeam } from "../../types/api";
import { IRegisterTeamForm } from "../../types/forms";
import { ILocalStorageUserData } from "../../types/users";

export const loader = async () => {
    let getTeams;
    const clubhouseTournaments = store.getState().clubhouse.tournaments;
    const masterTournaments =
        store.getState().tournamentmanagement.masterTournaments;
    const tournaments = store.getState().tournamentmanagement.tournaments;
    const auth = store.getState().auth;
    const settings = store.getState().global.settings;
    const products = store.getState().shop.products;
    const homeLeagues = store.getState().tournamentmanagement.homeLeagues;

    if (auth.loggedIn) {
        const user = store.getState().auth
            .user as ILocalStorageUserData["user"];

        const homeLeaguesPromise = homeLeagues.length
            ? Promise.resolve()
            : store.dispatch(tournamentManagementActions.getHomeLeagues());

        const clubhouseTeamsPromise =
            user.userLevel <= 2
                ? clubhouseTournaments.length
                    ? Promise.resolve()
                    : store.dispatch(clubhouseActions.getTournaments(user.id))
                : Promise.resolve();

        const tournamentsPromise = tournaments.length
            ? Promise.resolve()
            : store.dispatch(tournamentManagementActions.getTournaments());

        const masterTournamentsPromise = masterTournaments.length
            ? Promise.resolve()
            : store.dispatch(
                  tournamentManagementActions.getMasterTournaments(),
              );

        const settingsPromise = settings.length
            ? Promise.resolve()
            : store.dispatch(globalActions.getSettings());

        const productsPromise = products.length
            ? Promise.resolve()
            : store.dispatch(shopActions.getProducts());

        [getTeams] = await Promise.all([
            getClubhouseTeams(user.id, true),
            clubhouseTeamsPromise,
            settingsPromise,
            tournamentsPromise,
            masterTournamentsPromise,
            productsPromise,
            homeLeaguesPromise,
        ]);
    }

    if (auth.loggedIn && auth.user?.userLevel) {
        return {
            teams: getTeams,
        };
    } else {
        return null;
    }
};

const RegisterTeam = (): React.JSX.Element => {
    const loaderData = useLoaderData() as {
        teams?: IAPIResponse & {
            teams: ITeam[];
        };
    };

    const products = useAppSelector(selectProducts);
    const teams =
        loaderData.teams?.teams || useAppSelector(selectClubhouseTeams);

    const location = useLocation();
    const dispatch = useAppDispatch();
    const user = useAppSelector(state => state.auth.user);
    const { tournaments, masterTournaments } = useAppSelector(
        state => state.tournamentmanagement,
    );

    const cart = useAppSelector(selectCart);
    const coupon = useAppSelector(state => state.shop.promoCode);
    const homeLeagues = useAppSelector(
        state => state.tournamentmanagement.homeLeagues,
    );

    const [cartTotal, setCartTotal] = useState<number>(0);
    const [cartDiscount, setCartDiscount] = useState<number>(0);
    const [showAddress, setShowAddress] = useState<boolean>(false);
    const [paymentType, setPaymentType] = useState<string>("");
    const [tournament, setTournament] = useState<
        { value: number; label: string } | undefined
    >(undefined);
    const [tournamentTeams, setTournamentTeams] = useState<
        Array<Partial<ITeam>>
    >([]);
    const [orderNotes, setOrderNotes] = useState<string>("");
    const [availableAgeLevels, setAvailableAgeLevels] = useState<
        { value: number; label: string }[]
    >([]);
    const tournamentOfferings = masterTournaments.filter(t => {
        return new Date(t.startDate) > new Date() && t.active;
    });

    const [clientSecret, setClientSecret] = useState<string>();
    const stripeKey =
        process.env.BUILD_ENV === "production"
            ? "pk_live_QwrNY8LDtUC2kaUeoZQ6gYLU00tznCwgsy"
            : "pk_test_a3zYRdFAUxyR4dnJ91Cvbs9k";
    const stripePromise = loadStripe(stripeKey);
    const navigate = useNavigate();

    useEffect(() => {
        if (paymentType === "cc") {
            getPaymentIntent(cart, cartTotal, cartDiscount)
                .then(data => {
                    setClientSecret(data.clientSecret);
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem initializing the Stripe order`,
                            text: e,
                            dismissible: true,
                        }),
                    );
                });
        }
    }, [paymentType]);

    const stripeOptions: StripeElementsOptions = {
        clientSecret,
        appearance: {
            theme: "stripe",
        },
    };

    const termsAndConditions = useAppSelector(state =>
        selectSetting(state, "wibb_terms_conditions"),
    );

    const registerSchema = yup
        .object()
        .shape({
            firstName: yup.string().required("First Name is required"),
            lastName: yup.string().required("Last Name is required"),
            email: yup
                .string()
                .required("Email address is required")
                .email("Email is invalid"),
            phone: yup
                .string()
                .required("Phone number is required")
                .matches(PHONE_REGEXP, "Phone number is not valid"),
            teamType: yup
                .string()
                .oneOf(
                    ["New", "Returning"],
                    "Please select if you are registering a New or Returning team",
                )
                .required("Please select a new or Returning team"),
            newTeam: yup.string().when("teamType", {
                is: "New",
                then: schema => schema.required("Plase specify your team name"),
                otherwise: schema =>
                    schema
                        .optional()
                        .transform((curr, orig) => (orig === "" ? null : curr)),
            }),
            returningTeam: yup
                .object({
                    value: yup.number(),
                    label: yup.string(),
                })
                .when("teamType", {
                    is: "Returning",
                    then: schema => schema.required("Plase select your team"),
                    otherwise: schema => schema.optional(),
                }),
            tournament: yup
                .object()
                .required(
                    "Please select the tournament you wish to register for",
                )
                .shape({
                    value: yup.number().required(),
                    label: yup
                        .string()
                        .required(
                            "Please select the tournament you wish to register for",
                        ),
                }),
            ageLevel: yup
                .object()
                .required()
                .shape({
                    value: yup.number().required(),
                    label: yup.string().required("Age Level is required"),
                }),
            promoCode: yup
                .string()
                .optional()
                .transform((curr, orig) => (orig === "" ? null : curr)),
            toc: yup
                .boolean()
                .equals(
                    [true],
                    "You must accept the Tournament Registration Terms & Conditions",
                ),
            price: yup.number().required(),
            friday: yup.boolean().optional(),
            homeLeague: yup
                .object()
                .shape({
                    value: yup.number().required(),
                    label: yup.string().required(),
                })
                .optional()
                .nullable(),
            otherLeague: yup.string().optional(),
            city: yup
                .string()
                .required("Please specify the city your team is from"),
            state: yup
                .string()
                .max(2, "Please enter your state's abbreviation (WI, IL, etc)")
                .required("Please specify the state your team is from"),
        })
        .strict();

    const {
        register,
        handleSubmit,
        control,
        reset,
        resetField,
        watch,
        setValue,
        setError,
        clearErrors,
        getValues,
        formState: { errors },
    } = useForm<IRegisterTeamForm>({
        mode: "onChange",
        resolver: yupResolver(registerSchema),
        defaultValues: {
            firstName: user!.firstName || undefined,
            lastName: user!.lastName || undefined,
            email: user!.email,
            phone: user!.phone || undefined,
            city: user!.city || undefined,
            state: user!.state || undefined,
            ageLevel: {
                value: undefined,
                label: undefined,
            },
            teamType: undefined,
            promoCode: "",
            tournament: undefined,
        },
    });

    const recalculateCartTotal = () => {
        if (cart.length) {
            let total = cart.reduce((a, c) => {
                return a + c.price;
            }, 0);
            setCartTotal(total);
            setCartDiscount(0);

            if (coupon) {
                switch (coupon.promoCodeType) {
                    case "product":
                        cart.map(p => {
                            if (
                                (coupon.appliesTo === null &&
                                    coupon.excludes === null) ||
                                (coupon.appliesTo &&
                                    coupon.appliesTo.includes(
                                        p.ageLevel.value,
                                    )) ||
                                (coupon.excludes &&
                                    !coupon.excludes.includes(p.ageLevel.value))
                            ) {
                                switch (coupon.promoCodeCategory) {
                                    case "fixed":
                                        setCartDiscount(
                                            prev =>
                                                prev +
                                                parseInt(
                                                    coupon.discount.toString(),
                                                ),
                                        );
                                        break;
                                    case "percent":
                                        setCartDiscount(
                                            prev =>
                                                prev +
                                                p.price *
                                                    (parseInt(
                                                        coupon.discount.toString(),
                                                    ) /
                                                        100),
                                        );

                                        break;
                                }
                            }

                            return p;
                        });
                        break;
                    case "cart":
                        switch (coupon.promoCodeCategory) {
                            case "fixed":
                                setCartDiscount(
                                    parseInt(coupon.discount.toString()),
                                );
                                break;
                            case "percent":
                                setCartDiscount(
                                    total *
                                        (parseInt(coupon.discount.toString()) /
                                            100),
                                );
                                break;
                        }

                        break;
                }
            }
        } else {
            setCartDiscount(0);
            setCartTotal(0);
        }
    };

    const onSubmit: SubmitHandler<IRegisterTeamForm> = (data, e) => {
        e?.preventDefault();
        dispatch(shopActions.addToCart(data));
        reset();
        setValue("promoCode", data.promoCode);
        setTournament(undefined);
        setAvailableAgeLevels([]);
    };

    const onError: SubmitErrorHandler<IRegisterTeamForm> = data => {
        console.log(data);
    };

    const submitOrder = async () => {
        await createOrder(
            paymentType,
            cart,
            cartTotal,
            cartDiscount,
            orderNotes,
        )
            .then(response => {
                dispatch(shopActions.emptyCart());
                dispatch(shopActions.deletePromoCode());
                setOrderNotes("");
                reset();
                dispatch(
                    alertActions.success({
                        title: `Team(s) registered successfully`,
                        dismissible: true,
                        persist: true,
                    }),
                );
                navigate(`/thankyou?order_id=${response.order.id}`);
            })
            .catch(e => {
                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem registering the team(s).`,
                        text: e,
                        dismissible: true,
                    }),
                );
            });
    };

    const removeItem = (item: IRegisterTeamForm) => {
        dispatch(shopActions.removeItem(item));
        recalculateCartTotal();
    };

    const fetchPromo = async (couponCode: IPromoCode["code"]) => {
        if (
            couponCode.trim() !== "" &&
            (getValues("ageLevel.value") || cart.length)
        ) {
            setCartDiscount(0);
            getCoupon(couponCode)
                .then(response => {
                    if (response && response.coupon) {
                        dispatch(shopActions.setPromoCode(response.coupon));
                        if (cart.length) {
                            dispatch(
                                shopActions.updatePromoCode(
                                    response.coupon.code,
                                ),
                            );
                        }
                    }
                })
                .catch(e => {
                    dispatch(shopActions.deletePromoCode());
                    setValue("promoCode", "");
                    setError("promoCode", {
                        message: "Invalid promo code",
                    });
                });
        } else {
            dispatch(shopActions.deletePromoCode());
            dispatch(shopActions.updatePromoCode(""));
            clearErrors("promoCode");
        }
    };

    useEffect(() => {
        recalculateCartTotal();
    }, [coupon, cart]);

    const teamType = watch("teamType", undefined);

    const watchTournament = useWatch({
        control,
        name: "tournament.value",
    });
    const watchLeague = useWatch({
        control,
        name: "homeLeague.value",
    });
    const watchAgeLevel = useWatch({
        control,
        name: "ageLevel.value",
    });

    useEffect(() => {
        if (watchTournament) {
            setAvailableAgeLevels(() =>
                products
                    .filter(
                        p =>
                            p.meta.tournamentId &&
                            tournaments
                                .filter(t => t.parent === watchTournament)
                                .map(t => t.id)
                                .includes(p.meta.tournamentId),
                    )
                    .filter(p => {
                        return (
                            p.active === 1 &&
                            (p.startDate
                                ? new Date() > new Date(p.startDate)
                                : true) &&
                            (p.endDate
                                ? new Date() < new Date(p.endDate)
                                : true) &&
                            (p.qty !== null ? (p.qty > 0 ? true : false) : true)
                        );
                    })
                    .sort((a, b) => {
                        return parseInt(a.name) - parseInt(b.name);
                    })
                    .map(p => {
                        return {
                            value: parseInt(p.id.toString()),
                            label: `${p.name}`,
                        };
                    }),
            );
        }
    }, [watchTournament]);

    useEffect(() => {
        if (watchAgeLevel && watchTournament) {
            const ageLevel = products.find(p => p.id === watchAgeLevel)?.name;
            const teams = tournaments.find(
                t => t.parent === watchTournament && t.ageLevel === ageLevel,
            )?.teams;

            setTournamentTeams(teams || []);
        }
    }, [watchAgeLevel, watchTournament]);

    useEffect(() => {
        interface stateData {
            tournament: {
                id: number;
                name: string;
                startDate: string;
                endDate: string;
            };
        }

        if (location.state && location.state.tournament) {
            const { tournament } = location.state as stateData;

            // Set selected tournament
            const tournamentValue = {
                value: tournament.id,
                label: `${tournament.name} ${defaultDateFormat.format(new Date(tournament.startDate))} - ${defaultDateFormat.format(new Date(tournament.endDate))}`,
            };
            setValue("tournament", tournamentValue);
            setTournament(tournamentValue);
        }

        if (location.hash) {
            const element = document.getElementById(
                location.hash.replace("#", ""),
            );
            if (element) {
                element.scrollIntoView({
                    behavior: "smooth",
                });
            }
        }
    }, [location]);

    return (
        <div data-testid="registerContent">
            <Container className="register">
                <Form
                    onSubmit={handleSubmit(onSubmit, onError)}
                    id="registerForm"
                    data-testid="registerTeamForm"
                    autoComplete="false"
                >
                    <Row>
                        <Col md={6}>
                            <Form.Group
                                controlId="tournament"
                                id="tournamentRegistrationList"
                            >
                                <Form.Label>Tournament</Form.Label>
                                <Controller
                                    name="tournament"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={false}
                                            options={masterTournaments
                                                .filter(t => {
                                                    return (
                                                        t.active &&
                                                        new Date() <
                                                            new Date(
                                                                t.startDate,
                                                            )
                                                    );
                                                })
                                                .filter(t => {
                                                    return (
                                                        tournaments
                                                            .filter(
                                                                ct =>
                                                                    ct.parent ===
                                                                    t.id,
                                                            )
                                                            .filter(ct => {
                                                                return ct.maxTeams &&
                                                                    ct.teams
                                                                    ? ct.teams
                                                                          .length !==
                                                                          parseInt(
                                                                              ct.maxTeams.toString(),
                                                                          )
                                                                    : true;
                                                            }).length !== 0
                                                    );
                                                })
                                                .sort((a, b) => {
                                                    const aDate = new Date(
                                                        a.startDate,
                                                    ).getTime();
                                                    const bDate = new Date(
                                                        b.startDate,
                                                    ).getTime();

                                                    if (aDate - bDate !== 0) {
                                                        return aDate - bDate;
                                                    }

                                                    return a.name.localeCompare(
                                                        b.name,
                                                    );
                                                })
                                                .map(t => {
                                                    return {
                                                        value: parseInt(
                                                            t.id.toString(),
                                                        ),
                                                        label: `${t.name} ${defaultDateFormat.format(new Date(t.startDate))} - ${defaultDateFormat.format(new Date(t.endDate))}`,
                                                    };
                                                })}
                                            onChange={value => {
                                                if (value) {
                                                    setTournament(value);
                                                    resetField("ageLevel");
                                                    resetField("teamType");
                                                    resetField("newTeam");
                                                    resetField("returningTeam");
                                                }
                                                field.onChange(value);
                                            }}
                                        />
                                    )}
                                />
                                {errors.tournament &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: errors.tournament.label
                                            ?.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    {tournament && (
                        <Row>
                            <Col md={4}>
                                <Form.Group controlId="ageLevel">
                                    <Form.Label>Age Level</Form.Label>
                                    {availableAgeLevels.length ? (
                                        <Controller
                                            name="ageLevel"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    isSearchable={false}
                                                    options={availableAgeLevels}
                                                    onChange={value => {
                                                        const productPrice =
                                                            parseFloat(
                                                                (
                                                                    products.find(
                                                                        p =>
                                                                            value?.value ===
                                                                            p.id,
                                                                    )
                                                                        ?.price as number
                                                                ).toString(),
                                                            );
                                                        setValue(
                                                            "price",
                                                            productPrice,
                                                        );
                                                        field.onChange(value);
                                                    }}
                                                />
                                            )}
                                        />
                                    ) : (
                                        <h4>
                                            All Age Levels for this tournament
                                            are currently sold out
                                        </h4>
                                    )}
                                    {errors.ageLevel &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.ageLevel.label
                                                ?.message as string,
                                        })}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group controlId="teamType">
                                    <Form.Label>
                                        Are you registering a New or Returning
                                        Team*
                                    </Form.Label>
                                    <br />
                                    <Form.Control
                                        type="hidden"
                                        {...register("teamType", {
                                            value: undefined,
                                        })}
                                        aria-invalid={
                                            errors.teamType ? "true" : "false"
                                        }
                                    />
                                    <ButtonGroup>
                                        <Button
                                            variant={
                                                teamType === "New"
                                                    ? "primary"
                                                    : "outline-primary"
                                            }
                                            onClick={() => {
                                                setValue("teamType", "New", {
                                                    shouldValidate: true,
                                                });
                                                resetField("returningTeam");
                                            }}
                                            disabled={
                                                !availableAgeLevels.length
                                            }
                                        >
                                            New
                                        </Button>
                                        <Button
                                            variant={
                                                teamType === "Returning"
                                                    ? "primary"
                                                    : "outline-primary"
                                            }
                                            onClick={() => {
                                                setValue(
                                                    "teamType",
                                                    "Returning",
                                                    {
                                                        shouldValidate: true,
                                                    },
                                                );
                                                resetField("newTeam");
                                            }}
                                            disabled={
                                                !availableAgeLevels.length
                                            }
                                        >
                                            Returning
                                        </Button>
                                    </ButtonGroup>
                                    {errors.teamType &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.teamType
                                                .message as string,
                                        })}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                {teamType === "New" && (
                                    <Form.Group as={Col} controlId="newTeam">
                                        <Form.Label>New Team *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            {...register("newTeam")}
                                            aria-invalid={
                                                errors.newTeam
                                                    ? "true"
                                                    : "false"
                                            }
                                            disabled={
                                                !availableAgeLevels.length
                                            }
                                        />
                                        {errors.newTeam &&
                                            displayFormAlert({
                                                level: "danger",
                                                text: errors.newTeam
                                                    .message as string,
                                            })}
                                    </Form.Group>
                                )}
                                {teamType === "Returning" && (
                                    <Form.Group as={Col} controlId="newTeam">
                                        <Form.Label>
                                            Returning Team *
                                        </Form.Label>
                                        <Controller
                                            name="returningTeam"
                                            control={control}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    isSearchable={true}
                                                    options={teams
                                                        .filter(t => {
                                                            return (
                                                                !cart.some(
                                                                    i =>
                                                                        i
                                                                            .returningTeam
                                                                            ?.value ===
                                                                            t.id &&
                                                                        i
                                                                            .tournament
                                                                            .value ===
                                                                            tournament.value,
                                                                ) &&
                                                                !tournamentTeams?.some(
                                                                    i =>
                                                                        i.id ===
                                                                        t.id,
                                                                )
                                                            );
                                                        })
                                                        .map(t => {
                                                            // Get most recent ageLevel for team
                                                            const ageLevel =
                                                                t.ageLevels.reduce(
                                                                    (
                                                                        prev,
                                                                        current,
                                                                    ) => {
                                                                        return new Date(
                                                                            current.year,
                                                                            current.season.id,
                                                                        ) >
                                                                            new Date(
                                                                                prev.year,
                                                                                prev.season.id,
                                                                            )
                                                                            ? current
                                                                            : prev;
                                                                    },
                                                                    t
                                                                        .ageLevels[0],
                                                                );

                                                            return {
                                                                value: t.id,
                                                                label: `${t.name} ${ageLevel?.ageLevel} - (${t.coach.firstName} ${t.coach.lastName} - ${t.coach.email})`,
                                                            };
                                                        })}
                                                    isDisabled={
                                                        !availableAgeLevels.length
                                                    }
                                                />
                                            )}
                                        />

                                        {errors.returningTeam &&
                                            displayFormAlert({
                                                level: "danger",
                                                text: errors.returningTeam
                                                    .message as string,
                                            })}
                                    </Form.Group>
                                )}
                            </Col>
                        </Row>
                    )}

                    <Row>
                        <Form.Group as={Col} controlId="firstName">
                            <Form.Label>First Name *</Form.Label>
                            <Form.Control
                                type="text"
                                {...register("firstName")}
                                aria-invalid={
                                    errors.firstName ? "true" : "false"
                                }
                                disabled={!availableAgeLevels.length}
                            />
                            {errors.firstName &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.firstName.message as string,
                                })}
                        </Form.Group>
                        <Form.Group as={Col} controlId="lastName">
                            <Form.Label>Last Name *</Form.Label>
                            <Form.Control
                                type="text"
                                {...register("lastName")}
                                aria-invalid={
                                    errors.lastName ? "true" : "false"
                                }
                                disabled={!availableAgeLevels.length}
                            />
                            {errors.lastName &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.lastName.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group as={Col} md={6} controlId="email">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                {...register("email")}
                                aria-invalid={errors.email ? "true" : "false"}
                                disabled={!availableAgeLevels.length}
                            />
                            {errors.email &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.email.message as string,
                                })}
                        </Form.Group>
                        <Col md={6}>
                            <Form.Group controlId="phone">
                                <Form.Label>Phone *</Form.Label>
                                <Form.Control
                                    type="text"
                                    {...register("phone")}
                                    aria-invalid={
                                        errors.phone ? "true" : "false"
                                    }
                                    disabled={!availableAgeLevels.length}
                                />
                                {errors.phone &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: errors.phone.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Form.Group as={Col} xs={6} lg={4} controlId="city">
                            <Form.Label>Team City *</Form.Label>
                            <Form.Control
                                type="text"
                                {...register("city")}
                                aria-invalid={
                                    errors.promoCode ? "true" : "false"
                                }
                                disabled={!availableAgeLevels.length}
                            />
                            {errors.city &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.city.message as string,
                                })}
                            <Form.Text>
                                We do our best to avoid Friday night/early
                                Saturday morning games for teams with extended
                                travel distances
                            </Form.Text>
                        </Form.Group>
                        <Form.Group as={Col} xs={6} lg={2} controlId="state">
                            <Form.Label>Team State *</Form.Label>
                            <Form.Control
                                type="text"
                                {...register("state")}
                                aria-invalid={
                                    errors.promoCode ? "true" : "false"
                                }
                                disabled={!availableAgeLevels.length}
                            />
                            {errors.state &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.state.message as string,
                                })}
                        </Form.Group>
                        <Form.Group
                            as={Col}
                            xs={12}
                            lg={6}
                            controlId="homeLeague"
                        >
                            <Form.Label>
                                If your team competes in a regular league,
                                please select it below
                            </Form.Label>
                            <Row>
                                <Col xs={watchLeague === 0 ? 6 : 12}>
                                    <Controller
                                        name="homeLeague"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                options={[
                                                    ...homeLeagues.map(l => {
                                                        return {
                                                            value: l.id,
                                                            label: l.name,
                                                        };
                                                    }),
                                                    {
                                                        value: 0,
                                                        label: "Other (Enter League Name)",
                                                    },
                                                ]}
                                                isClearable={true}
                                                isDisabled={
                                                    !availableAgeLevels.length
                                                }
                                            />
                                        )}
                                    />
                                </Col>
                                {watchLeague === 0 && (
                                    <Col xs={6}>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter your team's home league"
                                            {...register("otherLeague")}
                                            aria-invalid={
                                                errors.promoCode
                                                    ? "true"
                                                    : "false"
                                            }
                                            disabled={
                                                !availableAgeLevels.length
                                            }
                                        />
                                    </Col>
                                )}
                            </Row>
                            <Form.Text>
                                We do our best to avoid matching up teams from
                                the same league during round-robin play
                            </Form.Text>
                        </Form.Group>
                    </Row>
                    <Row>
                        <Col xs={6}>
                            <Form.Group controlId="friday" className="mt-4">
                                <Form.Check
                                    inline
                                    label="Please check this box if you are not available to play Friday night"
                                    type="checkbox"
                                    className="checkbox-xl"
                                    {...register("friday")}
                                    aria-invalid={
                                        errors.friday ? "true" : "false"
                                    }
                                    disabled={!availableAgeLevels.length}
                                />
                            </Form.Group>
                        </Col>
                        <Form.Group as={Col} md={6} controlId="promoCode">
                            <Form.Label>
                                If you have a Promo code, please apply it below.
                            </Form.Label>
                            <Form.Control
                                type="text"
                                {...register("promoCode")}
                                aria-invalid={
                                    errors.promoCode ? "true" : "false"
                                }
                                onBlur={e => fetchPromo(e.currentTarget.value)}
                            />
                            {errors.promoCode &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.promoCode.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group as={Col} xs={6} controlId="comment">
                            <Form.Label>Order Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={10}
                                cols={50}
                                value={orderNotes}
                                onChange={e => {
                                    setOrderNotes(e.currentTarget.value);
                                }}
                            />
                            <Form.Text>
                                Use this area if there is anything you need to
                                let us know about prior to the tournament or if
                                you have a credit from a previous tournament to
                                use.
                            </Form.Text>
                        </Form.Group>
                    </Row>
                    <Row className="tandc">
                        <Col>
                            <div
                                className="no-bottom-pad"
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        termsAndConditions?.setting,
                                    ),
                                }}
                            />
                            <Form.Group controlId="toc">
                                <Form.Check
                                    inline
                                    label="I HAVE READ AND ACCEPTED THE LEAGUE'S
                                    REGISTRATION TEAMS AND CONDITIONS"
                                    type="checkbox"
                                    className="checkbox-xl"
                                    {...register("toc")}
                                    aria-invalid={errors.toc ? "true" : "false"}
                                    disabled={!availableAgeLevels.length}
                                />
                                {errors.toc &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: errors.toc.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={!availableAgeLevels.length}
                            >
                                Add to Cart
                            </Button>
                        </Col>
                    </Row>
                </Form>
                {cart?.length !== 0 && (
                    <Row>
                        <Col>
                            <section id="cart">
                                <h4>Your Order</h4>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>Age Level</th>
                                            <th>Team Type</th>
                                            <th>Team Name</th>
                                            <th>Price</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cart.map((c, idx) => {
                                            const tournament =
                                                masterTournaments.find(
                                                    t =>
                                                        c.tournament.value ===
                                                        t.id,
                                                );

                                            return (
                                                <tr key={idx}>
                                                    <td>{tournament?.name}</td>
                                                    <td>
                                                        {c.ageLevel.label}
                                                        {coupon &&
                                                        coupon.promoCodeType ===
                                                            "product" &&
                                                        ((coupon.appliesTo &&
                                                            !coupon?.appliesTo?.includes(
                                                                c.ageLevel
                                                                    .value,
                                                            )) ||
                                                            (coupon.excludes &&
                                                                coupon?.excludes?.includes(
                                                                    c.ageLevel
                                                                        .value,
                                                                ))) ? (
                                                            <>
                                                                <br />
                                                                <b className="text-danger">
                                                                    Promo code
                                                                    does not
                                                                    apply to
                                                                    this item
                                                                </b>
                                                            </>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </td>
                                                    <td>{c.teamType}</td>
                                                    <td>
                                                        {c.teamType === "New"
                                                            ? c.newTeam
                                                            : c.returningTeam
                                                                  ?.label}
                                                    </td>
                                                    <td>${c.price}</td>
                                                    <td>
                                                        <CloseIcon
                                                            className="icon"
                                                            size="xl"
                                                            onClick={() =>
                                                                removeItem(c)
                                                            }
                                                            style={{
                                                                cursor: "pointer",
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {cartDiscount !== 0 && (
                                            <tr id="discount">
                                                <td colSpan={6}>
                                                    <h6 className="mt-3">
                                                        Total Discount:{" "}
                                                        {new Intl.NumberFormat(
                                                            "en-US",
                                                            {
                                                                style: "currency",
                                                                currency: "USD",
                                                            },
                                                        ).format(cartDiscount)}
                                                    </h6>
                                                </td>
                                            </tr>
                                        )}
                                        <tr id="subtotal">
                                            <td colSpan={6}>
                                                <h5>
                                                    Order Total:{" "}
                                                    {new Intl.NumberFormat(
                                                        "en-US",
                                                        {
                                                            style: "currency",
                                                            currency: "USD",
                                                        },
                                                    ).format(
                                                        cartTotal -
                                                            cartDiscount,
                                                    )}
                                                </h5>
                                            </td>
                                        </tr>
                                        <tr id="paymentOptions">
                                            <td colSpan={6}>
                                                <Form.Check
                                                    inline
                                                    label="Check payments"
                                                    name="paymentType"
                                                    type="radio"
                                                    value="check"
                                                    checked={
                                                        paymentType === "check"
                                                    }
                                                    id="paymentCheck"
                                                    onChange={e => {
                                                        setShowAddress(true);
                                                        setPaymentType(
                                                            e.target.value,
                                                        );
                                                    }}
                                                />
                                                <Form.Check
                                                    inline
                                                    label="Credit Card (Stripe)"
                                                    name="paymentType"
                                                    type="radio"
                                                    value="cc"
                                                    id="paymentCC"
                                                    checked={
                                                        paymentType === "cc"
                                                    }
                                                    onChange={e => {
                                                        setShowAddress(false);
                                                        setPaymentType(
                                                            e.target.value,
                                                        );
                                                    }}
                                                />
                                                {showAddress &&
                                                    paymentType === "check" && (
                                                        <>
                                                            <br />
                                                            <br />
                                                            <p>
                                                                Please send
                                                                check to:
                                                                <br />
                                                                Baseball365
                                                                <br />
                                                                PO Box 5614
                                                                <br />
                                                                Madison, WI
                                                                53705
                                                                <br />
                                                                Checks should be
                                                                payable to
                                                                Baseball365
                                                            </p>

                                                            <Button
                                                                variant="primary"
                                                                onClick={() =>
                                                                    submitOrder()
                                                                }
                                                            >
                                                                Submit Order
                                                            </Button>
                                                        </>
                                                    )}
                                                {paymentType === "cc" &&
                                                    clientSecret && (
                                                        <Elements
                                                            stripe={
                                                                stripePromise
                                                            }
                                                            options={
                                                                stripeOptions
                                                            }
                                                        >
                                                            <CheckoutForm />
                                                        </Elements>
                                                    )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </section>
                        </Col>
                    </Row>
                )}

                <TournamentOfferings
                    tournamentOfferings={tournamentOfferings}
                    tournaments={tournaments}
                />
            </Container>
        </div>
    );
};

export default RegisterTeam;
